import { FC, useState } from 'react'
import { toast } from 'react-toastify'
import { useOsTecnicos } from '../../../hooks/os'
import {
  createOsTecnicoDocUrl,
  deleteOsTecnico,
  getOsTecnico,
  saveOsTecnico,
} from '../../../Service/os'
import { OsTecnico } from '../../../types/os'
import {
  BD,
  Button,
  Card,
  Field,
  Input,
  PageSubtitle,
  PageTitle,
  Select,
  Table,
} from '../styles'

const EMPTY: Partial<OsTecnico> = { contaTipo: 'CORRENTE' }

const Overlay = ({ children, onClose }: { children: React.ReactNode; onClose: () => void }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    onClick={onClose}
  >
    <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', padding: 24 }} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
)

export const Tecnicos: FC = () => {
  const { tecnicos, mutate } = useOsTecnicos()
  const [form, setForm] = useState<Partial<OsTecnico> | null>(null)
  const [saving, setSaving] = useState(false)
  const [docView, setDocView] = useState<string | null>(null) // signed URL do doc atual
  const [docFileName, setDocFileName] = useState<string | null>(null) // novo anexo (pré-save)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [loadingForm, setLoadingForm] = useState(false)

  const set = (k: keyof OsTecnico, v: any) => setForm((f) => ({ ...(f ?? {}), [k]: v }))

  const closeForm = () => {
    setForm(null)
    setDocView(null)
    setDocFileName(null)
    setLoadingForm(false)
  }

  const openNew = () => {
    setForm({ ...EMPTY })
    setDocView(null)
    setDocFileName(null)
    setLoadingForm(false)
  }
  const openEdit = async (id: number) => {
    // Abre o modal imediatamente (com loading) — evita a sensação de "travado"
    setForm({ id })
    setDocView(null)
    setDocFileName(null)
    setLoadingForm(true)
    try {
      const res = await getOsTecnico(id)
      setForm(res.data.data)
      setDocView(res.data.data.documentoUrlView ?? null)
    } catch {
      toast.error('Falha ao carregar técnico')
      closeForm()
    } finally {
      setLoadingForm(false)
    }
  }

  const buscarCep = async (cep?: string) => {
    const c = (cep ?? '').replace(/\D/g, '')
    if (c.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${c}/json/`)
      const d = await res.json()
      if (!d.erro) {
        setForm((f) => ({ ...(f ?? {}), logradouro: d.logradouro, bairro: d.bairro, cidade: d.localidade, uf: d.uf }))
      }
    } catch {
      /* CEP opcional — silencioso */
    }
  }

  const onDoc = async (file: File) => {
    setUploadingDoc(true)
    try {
      const { data } = await createOsTecnicoDocUrl(file.type)
      const put = await fetch(data.data.url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      set('documentoUrl', data.data.key)
      setDocFileName(file.name)
      setDocView(null)
      toast.success('Documento anexado — clique em Salvar para confirmar')
    } catch {
      toast.error('Falha no upload do documento')
    } finally {
      setUploadingDoc(false)
    }
  }

  const save = async () => {
    if (!form?.nome || !form?.cpf) {
      toast.warn('Nome e CPF são obrigatórios')
      return
    }
    setSaving(true)
    try {
      await saveOsTecnico(form)
      toast.success('Técnico salvo')
      closeForm()
      await mutate()
    } catch (e: any) {
      toast.error(e?.response?.data?.message === 'CPF_DUPLICADO' ? 'CPF já cadastrado' : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: number) => {
    if (!window.confirm('Inativar este técnico?')) return
    try {
      await deleteOsTecnico(id)
      toast.success('Técnico inativado')
      await mutate()
    } catch {
      toast.error('Erro ao inativar')
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <PageTitle>Gerenciamento de Técnicos</PageTitle>
          <PageSubtitle>Os técnicos cadastrados aqui aparecem no dropdown da OS.</PageSubtitle>
        </div>
        <Button onClick={openNew}>+ Novo técnico</Button>
      </div>

      <Card style={{ padding: 0, overflow: 'hidden', marginTop: 8 }}>
        <Table>
          <thead><tr><th>Nome</th><th>E-mail</th><th>Telefone</th><th style={{ textAlign: 'right' }}>Ações</th></tr></thead>
          <tbody>
            {tecnicos.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>Nenhum técnico cadastrado.</td></tr>
            )}
            {tecnicos.map((t) => (
              <tr key={t.id}>
                <td style={{ fontWeight: 500 }}>{t.nome}</td>
                <td>{t.email ?? '—'}</td>
                <td>{t.telefone ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <span style={{ color: BD.red, cursor: 'pointer', marginRight: 12 }} onClick={() => openEdit(t.id)}>editar</span>
                  <span style={{ color: BD.grayText, cursor: 'pointer' }} onClick={() => remove(t.id)}>inativar</span>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {form && (
        <Overlay onClose={() => closeForm()}>
          <h2 style={{ fontWeight: 800, fontSize: 18, marginBottom: 16 }}>{form.id ? 'Editar técnico' : 'Novo técnico'}</h2>
          {loadingForm ? (
            <div style={{ padding: 40, textAlign: 'center', color: BD.grayText }}>Carregando…</div>
          ) : (
            <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field><label>Nome *</label><Input value={form.nome ?? ''} onChange={(e) => set('nome', e.target.value)} /></Field>
            <Field><label>CPF *</label><Input value={form.cpf ?? ''} onChange={(e) => set('cpf', e.target.value)} /></Field>
            <Field><label>RG</label><Input value={form.rg ?? ''} onChange={(e) => set('rg', e.target.value)} /></Field>
            <Field><label>Nascimento</label><Input type="date" value={form.nascimento ?? ''} onChange={(e) => set('nascimento', e.target.value)} /></Field>
            <Field><label>Telefone</label><Input value={form.telefone ?? ''} onChange={(e) => set('telefone', e.target.value)} /></Field>
            <Field><label>E-mail</label><Input value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} /></Field>
            <Field><label>CEP</label><Input value={form.cep ?? ''} onChange={(e) => set('cep', e.target.value)} onBlur={(e) => buscarCep(e.target.value)} /></Field>
            <Field><label>Logradouro</label><Input value={form.logradouro ?? ''} onChange={(e) => set('logradouro', e.target.value)} /></Field>
            <Field><label>Número</label><Input value={form.numero ?? ''} onChange={(e) => set('numero', e.target.value)} /></Field>
            <Field><label>Bairro</label><Input value={form.bairro ?? ''} onChange={(e) => set('bairro', e.target.value)} /></Field>
            <Field><label>Cidade</label><Input value={form.cidade ?? ''} onChange={(e) => set('cidade', e.target.value)} /></Field>
            <Field><label>UF</label><Input maxLength={2} value={form.uf ?? ''} onChange={(e) => set('uf', e.target.value.toUpperCase())} /></Field>
            <Field><label>Banco</label><Input value={form.banco ?? ''} onChange={(e) => set('banco', e.target.value)} /></Field>
            <Field><label>Agência</label><Input value={form.agencia ?? ''} onChange={(e) => set('agencia', e.target.value)} /></Field>
            <Field><label>Conta</label><Input value={form.conta ?? ''} onChange={(e) => set('conta', e.target.value)} /></Field>
            <Field>
              <label>Tipo de conta</label>
              <Select value={form.contaTipo ?? 'CORRENTE'} onChange={(e) => set('contaTipo', e.target.value)}>
                <option value="CORRENTE">Corrente</option>
                <option value="POUPANCA">Poupança</option>
              </Select>
            </Field>
            <Field style={{ gridColumn: '1 / -1' }}>
              <label>Documento com foto</label>
              {docView && (
                <div style={{ marginBottom: 4 }}>
                  <a href={docView} target="_blank" rel="noreferrer" style={{ color: BD.red, fontSize: 13 }}>
                    📎 Ver documento atual
                  </a>
                </div>
              )}
              {docFileName && (
                <div style={{ fontSize: 12, color: '#065F46', marginBottom: 4 }}>
                  ✓ Novo documento anexado: {docFileName} — salve para confirmar.
                </div>
              )}
              <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => e.target.files?.[0] && onDoc(e.target.files[0])} />
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button $variant="secondary" onClick={() => closeForm()}>Cancelar</Button>
            <Button onClick={save} disabled={saving || uploadingDoc}>
              {uploadingDoc ? 'Enviando documento…' : 'Salvar'}
            </Button>
          </div>
            </>
          )}
        </Overlay>
      )}
    </>
  )
}

export default Tecnicos
